import { useState, useCallback } from 'react';
import { apiClient, getAccessToken, ApiRequestError } from '../api/client';
import type { AlteracoesDoCertificado } from '../utils/certificateFolders';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

export interface CertificateAccess {
  user_id: string;
  name: string;
  email: string;
  granted_at: string;
}

/** Só metadados: o servidor nunca devolve arquivo, senha ou chave. */
export interface Certificate {
  id: string;
  label: string;
  holder_id: string | null;
  holder_name: string | null;
  subject_cn: string;
  subject_document: string | null;
  issuer_cn: string;
  serial_number: string;
  fingerprint_sha256: string;
  not_before: string;
  not_after: string;
  days_to_expire: number;
  active: boolean;
  revoked_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  can_manage: boolean;
  can_use: boolean;
  access: CertificateAccess[];
}

export interface CertificateUploadResult extends Certificate {
  /** Preenchido pelo servidor quando o certificado vence em menos de 30 dias. */
  warning: string | null;
}

/**
 * fetch com o token do painel. O apiClient só envia JSON; o cadastro do certificado é
 * multipart (arquivo + senha). Se o token tiver expirado (401), uma chamada qualquer pelo
 * apiClient renova o token (ou leva ao login) e a requisição é refeita.
 */
async function fetchWithAuth(endpoint: string, init: RequestInit): Promise<Response> {
  const send = () => {
    const token = getAccessToken();
    return fetch(`${API_BASE}${endpoint}`, {
      ...init,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  };
  let response = await send();
  if (response.status === 401) {
    await apiClient.get('/users/me/membership');
    response = await send();
  }
  return response;
}

/** Mostra o `detail` que a API devolve (textos aprovados na D16); sem ele, uma mensagem genérica. */
async function errorMessage(response: Response, acao = 'cadastrar o certificado'): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.detail === 'string') return body.detail;
  } catch {
    // corpo não é JSON: usa a mensagem genérica abaixo
  }
  return `Erro ${response.status} ao ${acao}.`;
}

export function useCertificates() {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCertificates = useCallback(async () => {
    setIsLoading(true);
    try {
      setCertificates(await apiClient.get<Certificate[]>('/certificates'));
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar os certificados.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /** `dados` já é o formulário pronto (file, password, label). Nada é guardado aqui. */
  const uploadCertificate = useCallback(async (dados: FormData): Promise<CertificateUploadResult> => {
    const response = await fetchWithAuth('/certificates', { method: 'POST', body: dados });
    if (!response.ok) {
      throw new ApiRequestError(response.status, await errorMessage(response));
    }
    const result: CertificateUploadResult = await response.json();
    await fetchCertificates();
    return result;
  }, [fetchCertificates]);

  /**
   * Etapa 16: envia o PDF + finalidade (`dados` já pronto) e devolve o PDF assinado (Blob).
   * Nada é guardado aqui: quem chama entrega o arquivo como download e descarta.
   */
  const signPdf = useCallback(async (certificateId: string, dados: FormData): Promise<Blob> => {
    const response = await fetchWithAuth(`/certificates/${certificateId}/sign`, { method: 'POST', body: dados });
    if (!response.ok) {
      throw new ApiRequestError(response.status, await errorMessage(response, 'assinar o PDF'));
    }
    return response.blob();
  }, []);

  /** D23: muda rótulo e/ou titular (só os campos enviados; `holder_id: null` = "Sem titular"). */
  const updateCertificate = useCallback(async (id: string, alteracoes: AlteracoesDoCertificado) => {
    await apiClient.patch(`/certificates/${id}`, alteracoes);
    await fetchCertificates();
  }, [fetchCertificates]);

  const revokeCertificate = useCallback(async (id: string) => {
    await apiClient.delete(`/certificates/${id}`);
    await fetchCertificates();
  }, [fetchCertificates]);

  const grantAccess = useCallback(async (certificateId: string, userId: string) => {
    await apiClient.post(`/certificates/${certificateId}/access/${userId}`);
  }, []);

  const revokeAccess = useCallback(async (certificateId: string, userId: string) => {
    await apiClient.delete(`/certificates/${certificateId}/access/${userId}`);
  }, []);

  return {
    certificates,
    isLoading,
    error,
    fetchCertificates,
    uploadCertificate,
    signPdf,
    updateCertificate,
    revokeCertificate,
    grantAccess,
    revokeAccess,
  };
}
